
import { createAsyncRequestCache } from './asyncRequestCache';
import {
    extractClientProfileData,
    type ClientProfileData,
} from './clientProfile';
export type { ClientProfileData } from './clientProfile';

/**
 * Fetch campaign metadata by ID
 * @param campaignId - Campaign ID
 * @returns Campaign metadata, or null when the user is signed out or the
 *          campaign genuinely doesn't exist. Transport failures PROPAGATE so
 *          the campaign-metadata query enters error state and keeps its last
 *          successful cache instead of caching "deleted".
 */
export const fetchCampaignMetadata = async (campaignId: string) => {
    console.log(`📊 Fetching metadata from N8N for campaign: ${campaignId}`);

    // 🔥 N8N AS SINGLE SOURCE OF TRUTH
    const { fetchCampaignByIdFromN8N } = await import('./n8nDataFetchers');
    const { backendProvider } = await import('../services/backend');

    // Get current user email for N8N authentication
    const currentUser = backendProvider.auth.getCurrentUser();
    if (!currentUser) {
        console.error('❌ No authenticated user');
        return null;
    }

    console.log(`🔧 Fetching campaign metadata from N8N...`);

    // Fetch campaign from N8N (throws on transport failure — by design)
    const campaign = await fetchCampaignByIdFromN8N(campaignId, currentUser.email);

    if (!campaign) {
        console.log(`❌ Campaign ${campaignId} not found in N8N`);
        return null;
    }

    console.log(`✅ Campaign metadata loaded from N8N:`, campaign);
    return campaign;
};

/**
 * Fetch all campaigns with their leads count
 * @param userEmail - Optional user email to filter campaigns
 * @returns Array of campaigns with metadata and leads count
 */
/**
 * Fetch client profile information for the current user
 * Returns client_id, client_name, and client_email
 */
interface FetchClientProfileOptions {
    forceRefresh?: boolean;
}

const clientProfileRequestCache = createAsyncRequestCache<ClientProfileData | null>({
    ttlMs: 5 * 60 * 1000,
    shouldCache: (profile) => profile !== null,
});

const requestClientProfile = async (userEmail: string): Promise<ClientProfileData | null> => {
    try {
        const { n8nService } = await import('../components/core/services/n8n.service');

        console.log(`📥 Fetching client profile from N8N for: ${userEmail}`);

        // campaign_metrics is the authoritative (and only) source here: it carries
        // client identity + seller_ids + stripe_id. Live credits come separately
        // via get_credit_balance (appwriteQueryFns.profile.client / useClientProfile),
        // so they are intentionally NOT part of this profile.
        const campaignResponse = await n8nService.getCampaignMetrics({ client_email: userEmail });
        const profile = campaignResponse.success
            ? extractClientProfileData(campaignResponse.data, userEmail)
            : null;

        if (profile) return profile;

        // If no profile found, log warning and return null
        console.warn(`⚠️ No client profile found for ${userEmail} in N8N/MySQL`);
        return null;
    } catch (error: any) {
        console.error('❌ Error fetching client profile from N8N:', error);
        return null;
    }
};

export const fetchClientProfile = (
    userEmail: string,
    options: FetchClientProfileOptions = {},
): Promise<ClientProfileData | null> => {
    const cacheKey = userEmail.trim().toLowerCase();
    return clientProfileRequestCache.get(
        cacheKey,
        () => requestClientProfile(userEmail),
        options,
    );
};

export const fetchCampaignsWithCount = async (userEmail?: string) => {
    try {
        const { backendProvider } = await import('../services/backend');

        const currentUser = backendProvider.auth.getCurrentUser();
        if (!currentUser) {
            console.log('❌ No authenticated user');
            return [];
        }

        const email = userEmail || currentUser.email;
        const isAdmin = email?.toLowerCase() === 'brainiac@brainiacsbots.com' || email?.toLowerCase() === 'brainiacs@brainiacsbots.com';

        console.log(`📊 Fetching campaigns with count for ${email}`);

        let campaigns: any[];
        if (isAdmin) {
            // Admin sees all campaigns
            campaigns = await backendProvider.database.getCollection(
                'campaigns',
                [],
                { field: 'updatedAt', direction: 'desc' }
            );
        } else {
            // Fetch owned campaigns
            const ownedCampaigns = await backendProvider.database.getCollection(
                'campaigns',
                [{ field: 'client_email', operator: '==', value: email }],
                { field: 'updatedAt', direction: 'desc' }
            ).catch(() => []);

            // Fetch invited campaigns
            const invitedCampaigns = await backendProvider.database.getCollection(
                'campaigns',
                [{ field: 'invited_users', operator: 'array-contains', value: email }],
                { field: 'updatedAt', direction: 'desc' }
            ).catch(() => []);

            // Combine and deduplicate using N8N campaignId (not Appwrite document ID)
            const campaignMap = new Map();
            ownedCampaigns.forEach(campaign => {
                const n8nId = campaign.campaignId || campaign.campaign_id || campaign.$id;
                if (n8nId) campaignMap.set(n8nId, campaign);
            });
            invitedCampaigns.forEach(campaign => {
                const n8nId = campaign.campaignId || campaign.campaign_id || campaign.$id;
                if (n8nId) campaignMap.set(n8nId, campaign);
            });
            campaigns = Array.from(campaignMap.values());

            console.log(`📊 Found ${ownedCampaigns.length} owned + ${invitedCampaigns.length} invited = ${campaigns.length} unique campaigns`);
        }

        const campaignsWithCount = await Promise.all(
            campaigns.map(async (campaign) => {
                // Use campaignId field (N8N ID) for lead queries, not Appwrite doc ID
                // Fall back to campaign_id, $id (Appwrite doc ID) if campaignId is missing
                const campaignId = campaign.campaignId || campaign.campaign_id || campaign.$id;
                const campaignData = campaign;

                // Get leads count for this campaign
                const leads = await backendProvider.database.getCollection(
                    'leads',
                    [{ field: 'campaignId', operator: '==', value: campaignId }]
                ).catch(() => []);

                // Handle nested structure: { campaign: {...}, client: {...} }
                let flattenedData = campaignData;
                if (campaignData && campaignData.campaign && typeof campaignData.campaign === 'object') {
                    // Extract client data if present
                    const clientData: any = {};
                    if (campaignData.client && typeof campaignData.client === 'object') {
                        clientData.client_id = campaignData.client.id || '';
                        clientData.client_name = campaignData.client.name || '';
                        clientData.client_email = campaignData.client.email || '';
                    }

                    flattenedData = {
                        ...campaignData.campaign,
                        ...clientData
                    };
                }

                // Ensure we have a valid id and name
                const result = {
                    id: campaignId,
                    ...flattenedData,
                    leadsCount: leads.length
                };

                // Log for debugging
                if (!result.name && !result.campaign_name) {
                    console.warn(`⚠️ Campaign ${campaignId} has no name field:`, Object.keys(flattenedData));
                }

                return result;
            })
        );

        console.log(`📊 Returning ${campaignsWithCount.length} campaigns with counts`);
        return campaignsWithCount;
    } catch (error: any) {
        console.error('❌ Error fetching campaigns with count:', error);
        return [];
    }
};
